import React, { useState, useEffect } from 'react';
import { adminService } from '../../services';
import { KYC_STATUS } from '../../constants/config';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/common/PageHeader';
import { formatDate } from '../../utils/formatters';

const UserManagement = () => {
  const { t, i18n } = useTranslation();
  const { currentUserFullDetails } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const filter = 'ALL';
  const [verifying, setVerifying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [startingReview, setStartingReview] = useState(false);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');


  // Staff registration form
  const [staffData, setStaffData] = useState({
    email: '',
    password: '',
    fullName: '',
    role: 'FINANCE_ADMIN',
    countryCode: '+977',
    phoneNumber: '',
  });
  const [creatingStaff, setCreatingStaff] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 10;

  const normalizeUser = (u) => {
    const rawStatus = String(u?.status || '').toUpperCase();
    const enabledFlag = (u?.isEnabled ?? u?.isActive ?? u?.enabled);
    const status =
      rawStatus === 'ACTIVE' || rawStatus === 'INACTIVE'
        ? rawStatus
        : enabledFlag === true
          ? 'ACTIVE'
          : enabledFlag === false
            ? 'INACTIVE'
            : 'ACTIVE'; // fallback: treat unknown as active to avoid false "inactive"

    // phone fields vary by endpoint; keep existing UI fields if present
    const mobileNo = String(u?.mobileNo || '').trim();
    const phoneMatch = mobileNo.match(/^\+(\d{1,3})(\d{6,14})$/);
    const countryCode = u?.countryCode || (phoneMatch ? `+${phoneMatch[1]}` : '');
    const phoneNumber = u?.phoneNumber || (phoneMatch ? phoneMatch[2] : mobileNo.replace(/\D/g, ''));

    return { ...u, status, countryCode, phoneNumber };
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        limit,
        offset: (currentPage - 1) * limit,
        sortBy: 'createdAt',
        order: 'DESC',
      };

      if (filter !== 'ALL') {
        params.kycStatus = filter;
      }

      const response = await adminService.getAllUsers(params);
      const usersList = response?.data?.items || response?.items || [];
      const total = response?.data?.totalCount || response?.totalCount || 0;

      setUsers(usersList.map(normalizeUser));
      setTotalCount(total);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(t('admin.users.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (user) => {
    setSelectedUser(user);
    setShowModal(true);
    setError('');
    setSuccess('');
    setShowRejectInput(false);
    setRejectionReason('');

    // Fetch latest user details to get startedReviewing, startedReviewBy etc.
    try {
      const response = await adminService.getUserDetails(user.id);
      const latestUserData = response.data || response;
      setSelectedUser(normalizeUser(latestUserData));

      // Update the user in the list as well
      setUsers(prevUsers => prevUsers.map(u => u.id === user.id ? normalizeUser({ ...u, ...latestUserData }) : u));
    } catch (err) {
      console.error('Error fetching user details:', err);
      // Continue with provided user data if fetch fails
    }
  };



  const handleStartReview = async () => {
    if (!selectedUser) return;

    setStartingReview(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminService.startReviewKyc(selectedUser.id);

      const updatedUser = response.data || response;
      const finalUpdatedUser = {
        ...updatedUser,
        kycStatus: updatedUser.kycStatus || KYC_STATUS.UNDER_REVIEW,
        startedReviewing: true,
        startedReviewBy: currentUserFullDetails?.id,
        startedReviewByName: currentUserFullDetails?.fullName
      };

      // Set success message for the main page (outside modal)
      setSuccess(t('admin.users.messages.reviewSuccess', { name: selectedUser.fullName }));

      // Update the user in the list
      setUsers(prevUsers => prevUsers.map(u => u.id === selectedUser.id ? { ...u, ...finalUpdatedUser } : u));

      // Update the selected user so the modal reflects the "Approve/Reject" buttons immediately
      setSelectedUser(prev => ({ ...prev, ...finalUpdatedUser }));

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error starting review:', err);
      setError(err.response?.data?.message || t('admin.users.messages.reviewError'));
    } finally {
      setStartingReview(false);
    }
  };

  const handleVerifyKyc = async (status) => {
    if (!selectedUser) return;

    // If rejecting and reason is not provided, show reason input
    if (status === KYC_STATUS.REJECTED && !showRejectInput) {
      setShowRejectInput(true);
      return;
    }

    if (status === KYC_STATUS.REJECTED && !rejectionReason.trim()) {
      setError(t('admin.users.messages.provideReason'));
      return;
    }

    setVerifying(true);
    setError('');
    setSuccess('');

    try {
      const payload = { status: status };
      if (status === KYC_STATUS.REJECTED) {
        payload.reason = rejectionReason;
      }

      await adminService.verifyUserKyc(selectedUser.id, payload);
      setSuccess(t('admin.users.messages.verifySuccess', { status: t(`admin.users.kyc.status.${status.toLowerCase()}`) }));

      // If we are filtering by a specific status, remove the user from the list since their status has changed
      if (filter !== 'ALL' && filter !== status) {
        setUsers(prevUsers => prevUsers.filter(u => u.id !== selectedUser.id));
        setTotalCount(prev => prev - 1);
      } else {
        // Otherwise update the user in the list
        setUsers(prevUsers => prevUsers.map(u => u.id === selectedUser.id ? { ...u, kycStatus: status } : u));
      }

      setSelectedUser(prev => ({ ...prev, kycStatus: status }));
      setShowRejectInput(false);
      setRejectionReason('');

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowModal(false);
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Error verifying KYC:', err);
      setError(err.response?.data?.message || t('admin.users.messages.verifyError'));
    } finally {
      setVerifying(false);
    }
  };

  const handleDownloadUsers = async () => {
    setDownloading(true);
    setError('');

    try {
      const params = {};
      if (filter !== 'ALL') {
        params.kycStatus = filter;
      }

      const res = await adminService.downloadUsers(params);
      const blob = res?.data;

      // If backend returned JSON error as blob, show the message instead of downloading
      const contentType = res?.headers?.['content-type'] || blob?.type || '';
      if (contentType.includes('application/json')) {
        const text = await blob.text();
        try {
          const json = JSON.parse(text);
          throw new Error(json?.message || t('admin.users.messages.downloadError'));
        } catch (parseErr) {
          throw new Error(text || t('admin.users.messages.downloadError'));
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = res?.headers?.['content-disposition'] || '';
      const fileNameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const defaultExt = contentType.includes('text/csv') ? 'csv' : contentType.includes('excel') ? 'xlsx' : 'csv';
      a.download = fileNameMatch?.[1] || `users_${filter}_${new Date().toISOString().split('T')[0]}.${defaultExt}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccess(t('admin.users.messages.downloadSuccess'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error downloading users:', err);
      setError(err.message || t('admin.users.messages.downloadError'));
      setTimeout(() => setError(''), 3000);
    } finally {
      setDownloading(false);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    setError('');
    setSuccess('');

    try {
      // API expects ACTIVATE or DEACTIVATE, not ACTIVE/INACTIVE
      const action = currentStatus === 'ACTIVE' ? 'DEACTIVATE' : 'ACTIVATE';
      const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';

      await adminService.updateUserStatus(userId, { status: action });

      // Update user in list with the result status
      setUsers(users.map(u => u.id === userId ? { ...u, status: newStatus } : u));

      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser({ ...selectedUser, status: newStatus });
      }

      setSuccess(`User ${action.toLowerCase()}d successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating user status:', err);
      setError(err.response?.data?.message || 'Failed to update user status');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setError('');
    setSuccess('');

    try {
      await adminService.deleteUser(userToDelete.id);

      // Remove user from list
      setUsers(users.filter(u => u.id !== userToDelete.id));

      setSuccess('User deleted successfully!');
      setShowDeleteConfirm(false);
      setUserToDelete(null);

      if (selectedUser && selectedUser.id === userToDelete.id) {
        setShowModal(false);
        setSelectedUser(null);
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.message || 'Failed to delete user');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    setCreatingStaff(true);
    setError('');
    setSuccess('');

    try {
      await adminService.registerStaff(staffData);

      setSuccess(`${staffData.role.replace('_', ' ')} created successfully!`);
      // New staff likely appears on first page; refresh list
      setCurrentPage(1);
      fetchUsers();

      // Reset form
      setStaffData({
        email: '',
        password: '',
        fullName: '',
        role: 'FINANCE_ADMIN',
        countryCode: '+977',
        phoneNumber: '',
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowStaffModal(false);
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Error creating staff:', err);
      if (err.response?.data?.messages && Array.isArray(err.response.data.messages)) {
        const errorMessages = err.response.data.messages
          .map(item => `${item.field}: ${item.message} `)
          .join('\n');
        setError(errorMessages || err.response?.data?.message || t('admin.users.messages.creationError'));
      } else {
        setError(err.response?.data?.message || err.message || t('admin.users.messages.creationError'));
      }
    } finally {
      setCreatingStaff(false);
    }
  };

  const getKycStatusBadge = (status) => {
    const styles = {
      OPEN: 'bg-yellow-100 text-yellow-800',
      UNDER_REVIEW: 'bg-blue-100 text-blue-800',
      VERIFIED: 'bg-green-100 text-green-800',
      REJECTED: 'bg-red-100 text-red-800',
    };

    const labels = {
      OPEN: t('admin.users.kyc.status.open'),
      UNDER_REVIEW: t('admin.users.kyc.status.underReview'),
      VERIFIED: t('admin.users.kyc.status.verified'),
      REJECTED: t('admin.users.kyc.status.rejected'),
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const totalPages = Math.ceil(totalCount / limit);

  if (loading && users.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50 pb-8">
      <PageHeader
        title={t('admin.users.title')}
        subtitle={t('admin.users.subtitle')}
        icon={
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        }
      />
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-8 flex justify-end items-start">
          <div className="flex gap-3">
            <button
              onClick={handleDownloadUsers}
              disabled={downloading}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {downloading ? t('admin.users.buttons.downloading') : t('admin.users.buttons.downloadUsers')}
            </button>
            <button
              onClick={() => setShowStaffModal(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('admin.users.buttons.registerAdmin')}
            </button>
          </div>
        </div>

        {error && !showModal && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {success && !showModal && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {success}
          </div>
        )}

        {/* Filter Tabs - Commented Out
        <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
          {['ALL', 'OPEN', 'UNDER_REVIEW', 'VERIFIED', 'REJECTED'].map((status) => (
            <button
              key={status}
              onClick={() => {
                setFilter(status);
                setCurrentPage(1);
              }}
              className={`px - 4 py - 2 rounded - lg font - medium whitespace - nowrap transition ${
  filter === status
  ? 'bg-primary-600 text-white'
  : 'bg-white text-gray-700 hover:bg-gray-100'
} `}
            >
              {status.replace('_', ' ')}
            </button>
          ))}
        </div>
        */}

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-100 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.users.table.user')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.users.table.email')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.users.table.phone')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.users.table.status')}
                  </th>
                  {/* KYC Status Column - Commented Out
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    KYC Status
                  </th>
                  */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {filter === "VERIFIED" ? t('admin.users.table.verifiedBy') : filter === 'REJECTED' ? t('admin.users.table.rejectedBy') : t('admin.users.table.reviewingBy')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.users.table.registered')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.users.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      {t('admin.users.table.noUsers')}
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-primary-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{user.fullName}</div>
                        <div className="text-xs text-gray-500">{user.role || 'USER'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {user.countryCode} {user.phoneNumber}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleUserStatus(user.id, user.status)}
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${user.status === 'ACTIVE'
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                            } transition`}
                        >
                          {user.status === 'ACTIVE' ? t('admin.users.status.active') : t('admin.users.status.inactive')}
                        </button>
                      </td>
                      {/* KYC Status Column Data - Commented Out
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getKycStatusBadge(user.kycStatus)}
                      </td>
                      */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.kycStatus === KYC_STATUS.VERIFIED || user.kycStatus === KYC_STATUS.REJECTED
                          ? user.approvedByName || '-'
                          : user.startedReviewing
                            ? user.startedReviewByName || '-'
                            : t('common.na')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(user.createdAt, i18n.language)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleViewDetails(user)}
                            className="text-primary-600 hover:text-primary-900 transition"
                          >
                            {t('admin.users.buttons.view')}
                          </button>
                          <button
                            onClick={() => {
                              setUserToDelete(user);
                              setShowDeleteConfirm(true);
                            }}
                            className="text-red-600 hover:text-red-900 transition"
                          >
                            {t('admin.users.buttons.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-primary-50 disabled:opacity-50"
                >
                  {t('actions.previous')}
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-primary-50 disabled:opacity-50"
                >
                  {t('actions.next')}
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    {t('common.pagination.showing')} <span className="font-medium">{(currentPage - 1) * limit + 1}</span> {t('common.pagination.to')}{' '}
                    <span className="font-medium">{Math.min(currentPage * limit, totalCount)}</span> {t('common.pagination.of')}{' '}
                    <span className="font-medium">{totalCount}</span> {t('common.pagination.results')}
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {t('actions.previous')}
                    </button>
                    {(() => {
                      const maxButtons = 5;
                      let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
                      let endPage = Math.min(totalPages, startPage + maxButtons - 1);

                      if (endPage - startPage + 1 < maxButtons) {
                        startPage = Math.max(1, endPage - maxButtons + 1);
                      }

                      const pages = [];
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(i);
                      }

                      return pages.map((pageNum) => (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`relative inline - flex items - center px - 4 py - 2 border text - sm font - medium ${currentPage === pageNum
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            } `}
                        >
                          {pageNum}
                        </button>
                      ));
                    })()}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {t('actions.next')}
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User Details Modal */}
      {showModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">{t('admin.users.modal.userDetails')}</h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setShowRejectInput(false);
                  setRejectionReason('');
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                {success}
              </div>
            )}

            <div className="max-h-[70vh] overflow-y-auto">
              {/* Personal Information */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">{t('admin.users.modal.personalInfo')}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">{t('admin.users.modal.fullName')}</label>
                    <p className="text-gray-900">{selectedUser.fullName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">{t('admin.users.modal.dob')}</label>
                    <p className="text-gray-900">{selectedUser.dob ? formatDate(selectedUser.dob, i18n.language) : t('common.na')}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">{t('admin.users.modal.email')}</label>
                    <p className="text-gray-900">{selectedUser.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">{t('admin.users.modal.phone')}</label>
                    <p className="text-gray-900">{selectedUser.countryCode} {selectedUser.phoneNumber}</p>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">{t('admin.users.modal.address')}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">{t('admin.users.modal.province')}</label>
                    <p className="text-gray-900">{selectedUser.province || t('common.na')}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">{t('admin.users.modal.district')}</label>
                    <p className="text-gray-900">{selectedUser.district || t('common.na')}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">{t('admin.users.modal.municipality')}</label>
                    <p className="text-gray-900">{selectedUser.municipality || t('common.na')}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">{t('admin.users.modal.ward')}</label>
                    <p className="text-gray-900">{selectedUser.ward || t('common.na')}</p>
                  </div>
                </div>
              </div>

              {/* Citizenship Information */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">{t('admin.users.modal.citizenshipInfo')}</h4>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">{t('admin.users.modal.citizenshipNumber')}</label>
                    <p className="text-gray-900">{selectedUser.citizenshipNumber || t('common.na')}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">{t('admin.users.modal.issueDate')}</label>
                    <p className="text-gray-900">
                      {selectedUser.citizenshipIssueDate ? formatDate(selectedUser.citizenshipIssueDate, i18n.language) : t('common.na')}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">{t('admin.users.modal.issueDistrict')}</label>
                    <p className="text-gray-900">{selectedUser.citizenshipIssueDistrict || t('common.na')}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">{t('admin.users.modal.kycStatus')}</label>
                    <div className="mt-1">{getKycStatusBadge(selectedUser.kycStatus)}</div>
                  </div>
                  {(selectedUser.approvedByName || selectedUser.startedReviewing) && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {selectedUser.kycStatus === KYC_STATUS.VERIFIED || selectedUser.kycStatus === KYC_STATUS.REJECTED ? t('admin.users.modal.actionTakenBy') : t('admin.users.modal.reviewedBy')}
                      </label>
                      <p className="text-gray-900 font-medium">
                        {selectedUser.kycStatus === KYC_STATUS.VERIFIED || selectedUser.kycStatus === KYC_STATUS.REJECTED
                          ? selectedUser.approvedByName
                          : selectedUser.startedReviewByName}
                      </p>
                    </div>
                  )}
                </div>

                {/* Citizenship Documents */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 block mb-2">{t('admin.users.modal.citizenshipFront')}</label>
                    {selectedUser.citizenshipFrontKeyUrl ? (
                      <a href={selectedUser.citizenshipFrontKeyUrl} target="_blank" rel="noopener noreferrer">
                        <img
                          src={selectedUser.citizenshipFrontKeyUrl}
                          alt={t('admin.users.modal.citizenshipFront')}
                          className="w-full h-48 object-cover rounded-lg border border-gray-300 hover:opacity-90 cursor-pointer"
                        />
                      </a>
                    ) : (
                      <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500">
                        {t('admin.users.modal.noImage')}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 block mb-2">{t('admin.users.modal.citizenshipBack')}</label>
                    {selectedUser.citizenshipBackKeyUrl ? (
                      <a href={selectedUser.citizenshipBackKeyUrl} target="_blank" rel="noopener noreferrer">
                        <img
                          src={selectedUser.citizenshipBackKeyUrl}
                          alt={t('admin.users.modal.citizenshipBack')}
                          className="w-full h-48 object-cover rounded-lg border border-gray-300 hover:opacity-90 cursor-pointer"
                        />
                      </a>
                    ) : (
                      <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500">
                        {t('admin.users.modal.noImage')}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* KYC History Section */}
              <div className="mb-6 pt-6 border-t border-gray-100">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.users.modal.kycHistory')}</h4>
                {((selectedUser.kycHistory || selectedUser.history) && (selectedUser.kycHistory || selectedUser.history).length > 0) ? (
                  <div className="space-y-4">
                    {(selectedUser.kycHistory || selectedUser.history).slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((item, index) => (
                      <div key={index} className="flex gap-4 items-start bg-primary-50/50 p-4 rounded-lg border border-primary-200">
                        <div className="flex-shrink-0 mt-1">
                          {item.status === 'REJECTED' ? (
                            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                              <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </div>
                          ) : item.status === 'VERIFIED' ? (
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="flex-grow">
                          <div className="flex justify-between items-start mb-1">
                            <span className={`text-sm font-bold ${item.status === 'REJECTED' ? 'text-red-700' :
                              item.status === 'VERIFIED' ? 'text-green-700' : 'text-blue-700'
                              }`}>
                              {item.status === 'VERIFIED' ? t('admin.users.kyc.status.verified') :
                                item.status === 'REJECTED' ? t('admin.users.kyc.status.rejected') :
                                  item.status === 'UNDER_REVIEW' ? t('admin.users.kyc.status.underReview') :
                                    item.status}
                            </span>
                            <span className="text-xs text-gray-500">
                              {new Date(item.createdAt).toLocaleString(i18n.language === 'ne' ? 'ne-NP' : 'en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          {item.reason && (
                            <p className="text-sm text-gray-600 leading-relaxed italic border-l-2 border-gray-200 pl-3 mt-1">
                              "{item.reason}"
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-primary-50/50 rounded-lg text-center text-primary-600/70 text-sm italic border border-primary-100">
                    {t('admin.users.modal.noHistory')}
                  </div>
                )}
              </div>

              {/* User Status Management */}
              {/* <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">User Status</h4>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleToggleUserStatus(selectedUser.id, selectedUser.status)}
                    className={`flex - 1 px - 4 py - 2 rounded - lg transition ${
  selectedUser.status === 'ACTIVE'
  ? 'bg-yellow-600 text-white hover:bg-yellow-700'
  : 'bg-green-600 text-white hover:bg-green-700'
} `}
                  >
                    {selectedUser.status === 'ACTIVE' ? 'Deactivate User' : 'Activate User'}
                  </button>
                  <button
                    onClick={() => {
                      setUserToDelete(selectedUser);
                      setShowDeleteConfirm(true);
                    }}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
                  >
                    Delete User
                  </button>
                </div>
              </div> */}

              {/* KYC Verification Actions */}
              {(filter === 'OPEN' || filter === 'UNDER_REVIEW' || selectedUser.kycStatus === KYC_STATUS.OPEN || selectedUser.kycStatus === KYC_STATUS.UNDER_REVIEW) && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">KYC Verification</h4>

                  {selectedUser.startedReviewing && selectedUser.kycStatus === KYC_STATUS.UNDER_REVIEW ? (
                    <div className="space-y-4">
                      <div className="p-3 bg-blue-50 border border-blue-100 text-blue-800 rounded-lg text-sm flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {selectedUser.startedReviewBy === currentUserFullDetails?.id
                          ? 'You are currently reviewing this application.'
                          : `This application is currently being reviewed by ${selectedUser.startedReviewByName || 'another admin'}.`}
                      </div>

                      {selectedUser.startedReviewBy === currentUserFullDetails?.id && (
                        <div className="space-y-4">
                          {showRejectInput ? (
                            <div className="bg-red-50 p-4 rounded-lg border border-red-100 animate-fadeIn">
                              <label className="block text-sm font-medium text-red-800 mb-2">
                                Reason for Rejection <span className="text-red-500">*</span>
                              </label>
                              <textarea
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                className="w-full px-3 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent min-h-[100px]"
                                placeholder="Please explain why this KYC is being rejected..."
                                required
                              />
                              <div className="flex gap-2 mt-3">
                                <button
                                  onClick={() => handleVerifyKyc(KYC_STATUS.REJECTED)}
                                  disabled={verifying}
                                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                                >
                                  {verifying ? 'Rejecting...' : 'Confirm Reject'}
                                </button>
                                <button
                                  onClick={() => {
                                    setShowRejectInput(false);
                                    setRejectionReason('');
                                  }}
                                  disabled={verifying}
                                  className="px-4 py-2 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex gap-3">
                              <button
                                onClick={() => handleVerifyKyc(KYC_STATUS.VERIFIED)}
                                disabled={verifying}
                                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {verifying ? t('admin.users.messages.processing') : t('admin.users.buttons.approveKyc')}
                              </button>
                              <button
                                onClick={() => setShowRejectInput(true)}
                                disabled={verifying}
                                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {verifying ? t('admin.users.messages.processing') : t('admin.users.buttons.rejectKyc')}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={handleStartReview}
                      disabled={startingReview}
                      className="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {startingReview ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Starting Review...
                        </>
                      ) : 'Start Reviewing'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Staff Registration Modal */}
      {showStaffModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">{t('admin.admins.form.createTitle')}</h3>
              <button
                onClick={() => {
                  setShowStaffModal(false);
                  setError('');
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {error.split('\n').map((line, index) => (
                  <div key={index} className="mb-1 last:mb-0">
                    {line}
                  </div>
                ))}
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                {success}
              </div>
            )}

            <form onSubmit={handleCreateStaff} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.admins.form.role')} <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={staffData.role}
                  onChange={(e) => setStaffData({ ...staffData, role: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="ADMIN">{t('admin.admins.form.roleAdminDesc')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.admins.form.fullName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={staffData.fullName}
                  onChange={(e) => setStaffData({ ...staffData, fullName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder={t('admin.admins.form.fullNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.admins.form.email')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={staffData.email}
                  onChange={(e) => setStaffData({ ...staffData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder={t('admin.admins.form.emailPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.admins.form.phone')} <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={staffData.countryCode}
                    readOnly
                    className="w-20 px-3 py-2 border border-primary-300 text-primary-700 rounded-lg bg-primary-50"
                  />
                  <input
                    type="tel"
                    required
                    value={staffData.phoneNumber}
                    onChange={(e) => setStaffData({ ...staffData, phoneNumber: e.target.value })}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t('admin.admins.form.phonePlaceholder')}
                    minLength={10}
                    maxLength={10}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.admins.form.password')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  value={staffData.password}
                  onChange={(e) => setStaffData({ ...staffData, password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder={t('admin.admins.form.passwordPlaceholder')}
                  minLength={8}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowStaffModal(false);
                    setError('');
                  }}
                  className="flex-1 px-4 py-2 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 transition-colors"
                >
                  {t('admin.admins.buttons.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={creatingStaff}
                  className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingStaff ? t('common.creating') : t('admin.users.buttons.registerAdmin')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && userToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">{t('admin.users.modal.confirmDeleteTitle')}</h3>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setUserToDelete(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-2">
                {t('admin.users.modal.confirmDeleteMessage')}
              </p>
              <div className="bg-primary-50/50 p-3 rounded-lg border border-primary-100">
                <p className="font-semibold text-gray-900">{userToDelete.fullName}</p>
                <p className="text-sm text-gray-600">{userToDelete.email}</p>
              </div>
              <p className="text-sm text-red-600 mt-3">
                ⚠️ {t('admin.users.modal.deleteWarning')}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setUserToDelete(null);
                }}
                className="flex-1 px-4 py-2 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 transition-colors"
              >
                {t('admin.admins.buttons.cancel')}
              </button>
              <button
                onClick={handleDeleteUser}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
              >
                {t('admin.users.buttons.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
